import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useSession } from '@/app/session-context';
import { NAV, NAV_SECTIONS, type NavItem } from '@/app/nav';
import { NAV_PREFETCH, isExactRoute } from '@/app/nav-meta';
import { useLeaveRequests } from '@/features/leave/api';
import { UnreadMessagesBadge } from '@/features/messaging/UnreadMessagesBadge';
import { cx } from '@/ui/primitives';

/**
 * The side navigation.
 *
 * Three things drove the redesign, in order of how much they cost a person:
 *
 * 1. **The active row was invisible.** `bg-surface-muted` on `bg-surface` is a
 *    1.09:1 contrast ratio in light and 1.13:1 in dark — below the 3:1 that
 *    WCAG 1.4.11 asks of a state indicator, and the same token used for hover
 *    AND for table headers. Hover also set `text-fg`, the active label colour,
 *    so once the pointer was in the menu the only remaining difference between
 *    "the page I'm on" and "the row I'm pointing at" was font-weight 500 vs 400
 *    at 14px. A stronger tint cannot fix this — every surface token in
 *    `tokens.css` sits within 1.15:1 of every other, so `bg-primary/12` only
 *    reaches 1.22:1. It takes a solid `bg-primary` fill: 8.57:1 light,
 *    5.33:1 dark. Applied through NavLink's `isActive` branch, so the hover
 *    classes are never emitted for the active row — the collision is not
 *    merely unlikely, it is unwritable.
 *
 * 2. **Twenty-one text-only rows is a list to read, not a menu to scan.** Icons
 *    give each destination a fixed shape and a position you can aim at. They
 *    are also what makes the collapsed rail legible at all.
 *
 * 3. **The column didn't fit.** Twenty-one rows plus five section headers is about
 *    900px; the 1366×768 laptops in this office leave ~600px below the header,
 *    so Yönetim sat permanently below the fold. It now scrolls inside itself,
 *    and collapses to a 56px rail — which also hands ~180px of width back to
 *    the wide tables on Puantaj and Ödeme Tablosu.
 *
 * Everything here is token colours only, no `dark:` variants, no new deps.
 */

const COLLAPSE_KEY = 'gh_nav_collapsed';
const SECTIONS_KEY = 'gh_nav_folded_sections';

/*
 * The rail is a preference, not a breakpoint.
 *
 * The tempting alternative is to force it between `md` and `lg`, where a 240px
 * column leaves 480px of content against `Table`'s 576px floor. Rejected:
 * `Table` already declares that it scrolls rather than squeezes, so forcing a
 * wordless menu trades information the user needs for a degradation the design
 * system has already accepted. It also produces the worse bug — a toggle that
 * visibly does nothing at some widths, or a tablet stuck in a rail it cannot
 * open. One boolean, honoured at every width the sidebar is visible at.
 */

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    /* Safari private mode throws on access — the rail is a preference, not a
       correctness concern, so falling back to "expanded" is fine. */
    return false;
  }
}

/**
 * The preference is read once, synchronously, in a lazy initializer.
 *
 * The tempting shape — `useState(false)` then a `useEffect` that reads storage —
 * paints a 240px column and reflows it to 56px one frame later on every single
 * page load. That is the only way to get a flash here, so it is worth naming.
 * No inline `<head>` script is needed either (unlike `gh_theme`): `#root` is
 * empty until React commits, and `App` holds a full-page spinner until the
 * session resolves, so nothing nav-shaped paints before this runs.
 */
function useNavCollapse(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* private mode */
    }
  }, [collapsed]);

  return [collapsed, useCallback(() => setCollapsed((v) => !v), [])];
}

function readFoldedSections(): Set<string> {
  try {
    return new Set((localStorage.getItem(SECTIONS_KEY) ?? '').split(',').filter(Boolean));
  } catch {
    /* private mode — same reasoning as readCollapsed */
    return new Set();
  }
}

/**
 * Which sections are folded shut, as a preference.
 *
 * A set of the CLOSED keys rather than the open ones, so a section added to
 * `NAV_SECTIONS` later arrives open. The inverse would hide a new section from
 * everyone who had ever touched this control, which is the kind of default that
 * gets reported as a missing feature.
 *
 * Read synchronously in a lazy initializer for the reason given above: the
 * alternative paints the full menu and folds it a frame later.
 */
function useSectionFold(): [Set<string>, (key: string) => void, (key: string) => void] {
  const [folded, setFolded] = useState(readFoldedSections);

  useEffect(() => {
    try {
      localStorage.setItem(SECTIONS_KEY, [...folded].join(','));
    } catch {
      /* private mode */
    }
  }, [folded]);

  const toggle = useCallback((key: string) => {
    setFolded((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);

  // Unfolding on navigation, not on every render: a person who lands on a page
  // inside a section they folded must still be able to SEE where they are. The
  // effect is keyed on the path, so folding the section you are already in
  // stays folded — it only reopens when you come back to it.
  const reveal = useCallback((key: string) => {
    setFolded((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  return [folded, toggle, reveal];
}

/** Active by the same rule `NavLink` uses below, so the two cannot disagree. */
function matchesRoute(to: string, pathname: string): boolean {
  if (isExactRoute(to)) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Pending approvals, as a leaf.
 *
 * Deliberately its own component so the query's re-render lands on one `<span>`
 * instead of twenty links and five sections. The key is byte-identical to the
 * one `ApprovalsPage` uses, so the two share a single cache entry: the badge
 * warms the page (clicking "Onaylarım" renders instantly) and opening the page
 * refreshes the badge. `mine=true` is filtered server-side to PENDING_APPROVAL
 * with an open step assigned to me or unassigned — so `meta.total` is exactly
 * "awaiting my decision", not "leave requests I can see".
 *
 * Only ever mounted for the Onaylarım row, which only renders when the user
 * holds LEAVE_REQUEST_APPROVE — so this never fires a request that 403s.
 */
function ApprovalsBadge({ labelled, active }: { labelled: boolean; active: boolean }) {
  const requests = useLeaveRequests({ mine: 'true' });
  const count = requests.data?.meta.total ?? 0;

  if (count === 0) return null;

  return (
    <>
      {/* One accessible name in both modes: the visual badge and the rail dot
          are both decorative, and this carries the meaning either way. */}
      <span className="sr-only">, {count} bekleyen</span>

      {labelled ? (
        <span
          aria-hidden="true"
          className={cx(
            'ms-auto inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-md px-1.5 text-[11px] leading-none font-semibold tabular-nums',
            // Inverted on the active pill so it stays readable against the fill.
            active ? 'bg-primary-fg text-primary' : 'bg-primary text-primary-fg',
          )}
        >
          {count > 99 ? '99+' : count}
        </span>
      ) : (
        // A "3" pill does not fit a 56px rail. The dot says "something is here";
        // the sr-only text above says how much.
        <span
          aria-hidden="true"
          className={cx(
            'pointer-events-none absolute top-1.5 right-1.5 size-2 rounded-full ring-2',
            active ? 'bg-primary-fg ring-primary' : 'bg-primary ring-surface',
          )}
        />
      )}
    </>
  );
}

interface RowProps {
  item: NavItem;
  labelled: boolean;
  onNavigate?: () => void;
  onTip?: (e: PointerEvent<HTMLElement>, label: string) => void;
  onTipEnd?: () => void;
  /** Drawer rows get a 44px target; the desktop column is pointer-driven. */
  touch?: boolean;
}

function NavRow({ item, labelled, onNavigate, onTip, onTipEnd, touch }: RowProps) {
  const Icon = item.icon;

  const base = cx(
    'relative flex w-full items-center gap-2.5 rounded-lg text-sm motion-safe:transition-colors',
    // Row height is identical in both modes on purpose: if the rail were 40px
    // and the column 36px, toggling would shift total content height by ~80px
    // and the scroll position would land somewhere else.
    touch ? 'min-h-11' : 'h-9',
    labelled ? 'px-2' : 'justify-center px-0',
  );

  const prefetch = () => void NAV_PREFETCH[item.key]?.();

  /*
   * Currently unreachable: every entry in `nav.ts` is `ready: true`.
   *
   * Kept deliberately. `ready` is the milestone affordance `nav.ts` declares —
   * a screen announced before it ships stays visible and disabled rather than
   * appearing out of nowhere. After the menus merged this is the only correct
   * implementation of it left (AppShell had a second, weaker copy), and it
   * carries a11y work that would have to be rewritten from scratch:
   * `aria-disabled` over `disabled` so the row keeps its place in the tab
   * order, and the muted badge pair at 5.16:1 rather than the warning colours
   * at 3.23:1. Deleting it to retire a dead branch costs more than it saves
   * the next time a screen is announced early.
   */
  if (!item.ready) {
    return (
      <li>
        {/* `aria-disabled`, not `disabled`: it stays in the tab order and is
            announced as unavailable, so a keyboard user learns the screen
            exists. The old markup used `opacity-55` (2.29:1 light — this is
            plain text, so the disabled-control exemption does not apply) and a
            `title`, which is hover-only: no keyboard, no touch. */}
        <span
          role="link"
          aria-disabled="true"
          tabIndex={0}
          className={cx(base, 'text-fg-muted cursor-not-allowed')}
        >
          <Icon size={18} className="shrink-0" aria-hidden="true" />
          {labelled && (
            <>
              <span className="flex-1 truncate">{item.label}</span>
              {/* Not `bg-warning/15 text-warning`, the obvious choice: that
                  measures 3.23:1 in light at 11px, a 1.4.3 failure. The muted
                  pair is 5.16:1 / 6.14:1 and reads correctly anyway — a row
                  that isn't ready yet should recede, not warn. */}
              <span className="bg-surface-muted text-fg-muted shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium">
                Yakında
              </span>
            </>
          )}
          {!labelled && <span className="sr-only">{item.label} — yakında</span>}
        </span>
      </li>
    );
  }

  return (
    <li>
      <NavLink
        to={item.to}
        end={isExactRoute(item.to)}
        onClick={onNavigate}
        onPointerEnter={(e) => {
          prefetch();
          if (!labelled) onTip?.(e, item.label);
        }}
        onPointerLeave={onTipEnd}
        onFocus={prefetch}
        onBlur={onTipEnd}
        className={({ isActive }) =>
          cx(
            base,
            isActive
              ? 'bg-primary text-primary-fg font-medium'
              : 'text-fg-muted hover:bg-surface-muted hover:text-fg',
          )
        }
      >
        {({ isActive }) => (
          <>
            <Icon size={18} className="shrink-0" aria-hidden="true" />
            {labelled ? (
              <span className="flex-1 truncate">{item.label}</span>
            ) : (
              <span className="sr-only">{item.label}</span>
            )}
            {item.key === 'approvals' && <ApprovalsBadge labelled={labelled} active={isActive} />}
            {/* Declared by the entry (`nav.ts`), not by a key match: a row with
                no `badge` mounts nothing and behaves exactly as before. */}
            {item.badge === 'unread-messages' && (
              <UnreadMessagesBadge labelled={labelled} active={isActive} />
            )}
          </>
        )}
      </NavLink>
    </li>
  );
}

interface ListProps {
  labelled: boolean;
  onNavigate?: () => void;
  onTip?: (e: PointerEvent<HTMLElement>, label: string) => void;
  onTipEnd?: () => void;
  touch?: boolean;
  /**
   * Rank for the section headings.
   *
   * The desktop column sits directly in the page, so `h2` is correct there. The
   * phone sheet does not: `Modal` renders its own title as an `h2`, so reusing
   * that rank inside makes the sections siblings of the dialog title rather
   * than its contents — walking by rank gives "Menü, İnsan kaynakları,
   * Misafirhane…" all on one level, which says the menu has no structure.
   *
   * A number rather than a tag name on purpose: a caller can only pick a rank,
   * not swap in an element that stops being a heading.
   */
  headingLevel?: 2 | 3;
}

function NavSections({
  labelled,
  onNavigate,
  onTip,
  onTipEnd,
  touch,
  headingLevel = 2,
}: ListProps) {
  const { can } = useSession();
  const visible = NAV.filter((item) => item.requires.every((p) => can(p)));
  const [folded, toggleFold, revealFold] = useSectionFold();
  const { pathname } = useLocation();

  // Resolved by comparison, not by building `h${n}`: a template literal widens
  // to `string`, and an arbitrary string in a JSX tag position is an intrinsic
  // element TypeScript will not check.
  const Heading = headingLevel === 3 ? 'h3' : 'h2';

  const activeSection = visible.find((item) => matchesRoute(item.to, pathname))?.section;
  useEffect(() => {
    if (activeSection) revealFold(activeSection);
  }, [activeSection, pathname, revealFold]);

  return (
    <>
      {NAV_SECTIONS.map((section, index) => {
        const items = visible.filter((item) => item.section === section.key);
        if (items.length === 0) return null;

        const headingId = `nav-bolum-${section.key}`;
        const listId = `nav-liste-${section.key}`;
        // Foldable only where there is something to click. In the rail the
        // headings are `sr-only`, so a fold there would hide rows behind a
        // control nobody can see — and `self` renders no heading at all.
        const foldable = Boolean(labelled && section.label);
        const isFolded = foldable && folded.has(section.key);

        return (
          <div
            key={section.key}
            className={cx(
              'mt-4 first:mt-0',
              // In the rail the headings are gone, so the grouping needs a
              // visible carrier. Expanded, the heading already does that job.
              !labelled && index > 0 && 'border-border mt-2 border-t pt-2',
            )}
          >
            {/* A real heading, not a decorative <p>. The old markup left five
                unnamed lists and twenty flat links in the a11y tree, and the
                `H` key found nothing inside the nav. It stays in the tree in
                rail mode too — only its pixels go away. */}
            {section.label && (
              <Heading
                id={headingId}
                className={cx(
                  'text-fg-muted text-[11px] font-semibold tracking-wider uppercase',
                  labelled ? 'mb-1' : 'sr-only',
                  !foldable && 'px-2',
                )}
              >
                {foldable ? (
                  <button
                    type="button"
                    onClick={() => toggleFold(section.key)}
                    aria-expanded={!isFolded}
                    aria-controls={listId}
                    // Same height and radius as a nav row so the heading does
                    // not become a second, taller kind of thing in the column.
                    className="hover:bg-surface-muted hover:text-fg focus-visible:ring-primary flex w-full items-center gap-1.5 rounded-lg px-2 py-1 text-left uppercase focus-visible:ring-2 focus-visible:outline-none motion-safe:transition-colors"
                  >
                    <ChevronRight
                      aria-hidden="true"
                      className={cx(
                        'size-3 shrink-0 motion-safe:transition-transform',
                        !isFolded && 'rotate-90',
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate">{section.label}</span>
                    {/* You are allowed to fold the section you are standing in
                        — but then the active row goes with it, and the menu
                        stops answering "where am I". The dot answers it without
                        reopening anything. */}
                    {isFolded && activeSection === section.key && (
                      <>
                        <span className="sr-only">, açık sayfa bu bölümde</span>
                        <span aria-hidden="true" className="bg-primary size-1.5 rounded-full" />
                      </>
                    )}
                    {/* The count is what makes a folded section still useful:
                        "Finans 7" tells you what is behind it without opening
                        it. Only while folded — expanded, the rows are the
                        count, and a number there is noise. */}
                    {isFolded && <span className="text-fg-muted text-[11px]">{items.length}</span>}
                  </button>
                ) : (
                  section.label
                )}
              </Heading>
            )}
            <ul
              id={listId}
              hidden={isFolded}
              className="space-y-0.5"
              {...(section.label ? { 'aria-labelledby': headingId } : { 'aria-label': 'Kişisel' })}
            >
              {items.map((item) => (
                <NavRow
                  key={item.key}
                  item={item}
                  labelled={labelled}
                  onNavigate={onNavigate}
                  onTip={onTip}
                  onTipEnd={onTipEnd}
                  touch={touch}
                />
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
}

/**
 * The always-labelled menu, for the phone sheet.
 *
 * Exported so the mobile `Modal` in `AppShell` renders the same rows, icons and
 * counts as the desktop column. Two hand-maintained copies of a
 * permissions-driven menu is how one of them ends up missing a section.
 *
 * `headingLevel` defaults to 3 because every caller so far is inside `Modal`,
 * which already owns the `h2`. It stays overridable rather than hard-coded: the
 * correct rank is a property of where the menu is mounted, and only the mounting
 * site knows that.
 */
export function NavMenuList({
  onNavigate,
  headingLevel = 3,
}: {
  onNavigate?: () => void;
  headingLevel?: 2 | 3;
}) {
  return <NavSections labelled onNavigate={onNavigate} touch headingLevel={headingLevel} />;
}

/** A rail tooltip, portalled to `<body>`.
 *
 *  It must not live inside the nav: the scroll region is `overflow-y-auto` and
 *  would clip it. `position: fixed` escapes that — but only while no ancestor
 *  establishes a containing block, which is why this component deliberately
 *  does not use `@container` anywhere (`container-type` computes to
 *  `contain: layout`, and fixed children would anchor to the 56px rail). */
function RailTooltip({ tip }: { tip: { label: string; top: number } | null }) {
  if (!tip) return null;

  return createPortal(
    <div
      // Purely visual: the row already carries the same text as its accessible
      // name via `sr-only`, so announcing it twice is noise.
      aria-hidden="true"
      style={{ top: tip.top }}
      className="border-border bg-surface text-fg pointer-events-none fixed left-16 z-50 -translate-y-1/2 rounded-lg border px-2.5 py-1.5 text-sm whitespace-nowrap shadow-lg"
    >
      {tip.label}
    </div>,
    document.body,
  );
}

export function SideNav() {
  const [collapsed, toggleCollapsed] = useNavCollapse();
  const [tip, setTip] = useState<{ label: string; top: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const labelled = !collapsed;

  const showTip = useCallback((e: PointerEvent<HTMLElement>, label: string) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ label, top: r.top + r.height / 2 });
  }, []);
  const hideTip = useCallback(() => setTip(null), []);

  // A fixed tooltip does not follow a scrolling anchor, so it has to go when
  // the rail moves under it.
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.addEventListener('scroll', hideTip, { passive: true });
    return () => node.removeEventListener('scroll', hideTip);
  }, [hideTip]);

  // Expanding while a tooltip is up would leave it floating beside a row that
  // now shows its own label. The pointer never leaves, so nothing else clears it.
  useEffect(() => hideTip(), [labelled, hideTip]);

  return (
    <>
      <nav
        aria-label="Ana menü"
        className={cx(
          'border-border bg-surface hidden shrink-0 flex-col border-r md:flex',
          // `self-start` is load-bearing: a flex item defaults to
          // `align-self: stretch`, and a stretched item already fills its
          // container, so `sticky` would have nothing to slide within and would
          // silently do nothing. The explicit height also defeats stretch —
          // keeping both means deleting one doesn't quietly break it.
          // 4.75rem is AppShell's md header height. Both numbers below are that
          // same height: `top` parks the column under the header, the calc keeps
          // it from running past the viewport. Change the header, change these.
          'md:sticky md:top-[4.75rem] md:h-[calc(100dvh-4.75rem)] md:self-start',
          labelled ? 'w-60' : 'w-14',
        )}
      >
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2 [scrollbar-gutter:stable]"
        >
          <NavSections labelled={labelled} onTip={showTip} onTipEnd={hideTip} />
        </div>

        {/* A `shrink-0` sibling of the scroll region, so the toggle can never
            scroll out of reach — which at ~900px of menu it otherwise would,
            exactly when you want it. */}
        <div className="border-border shrink-0 border-t p-2">
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Menüyü genişlet' : 'Menüyü daralt'}
            title={collapsed ? 'Menüyü genişlet' : 'Menüyü daralt'}
            className={cx(
              'text-fg-muted hover:bg-surface-muted hover:text-fg flex h-9 w-full items-center gap-2.5 rounded-lg text-sm transition',
              collapsed ? 'justify-center px-0' : 'px-2',
            )}
          >
            {collapsed ? (
              <PanelLeftOpen size={18} className="shrink-0" aria-hidden="true" />
            ) : (
              <PanelLeftClose size={18} className="shrink-0" aria-hidden="true" />
            )}
            {!collapsed && <span className="truncate">Menüyü daralt</span>}
          </button>
        </div>
      </nav>

      <RailTooltip tip={tip} />
    </>
  );
}
